import { ComponentProps, ReactNode, useMemo, useRef } from 'react';
import { ClerkProvider } from '@clerk/react';
import { useNavigate } from 'react-router-dom';

import { AppRoutes } from '@/utils/routes/router';

const LOG = '[ChurnSignal][ClerkNav]';

const CHOOSE_ORG_SIGN_UP = `${AppRoutes.signUp}/tasks/choose-organization`;
const CHOOSE_ORG_SIGN_IN = `${AppRoutes.signIn}/tasks/choose-organization`;

/**
 * Without routerPush/routerReplace, Clerk falls back to window.location for path
 * routing — full document reloads (OTP "flash") and races (choose-organization
 * canceled by hard nav to /).
 *
 * Clerk often invokes these during render. React Router 6 ignores navigate() in
 * that phase ("call navigate() in a React.useEffect()") — URL stays on /sign-up
 * with an empty SignUp card. Defer via setTimeout(0) + coalesce so the last
 * destination wins (queueMicrotask is still too early for RR6 listeners).
 */
function toSpaPath(to: string): string | null {
  if (!to.startsWith('http://') && !to.startsWith('https://')) {
    return to;
  }
  try {
    const url = new URL(to);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    /* external / invalid */
  }
  return null;
}

type Props = {
  publishableKey: string;
  children: ReactNode;
};

export default function ClerkProviderWithRouter({ publishableKey, children }: Props) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const { routerPush, routerReplace } = useMemo(() => {
    let pending: { path: string; replace: boolean } | null = null;
    let scheduled = false;

    const enqueue = (to: string, replace: boolean) => {
      const path = toSpaPath(to);
      if (path == null) {
        console.info(LOG, replace ? 'routerReplace → hard (external)' : 'routerPush → hard (external)', to);
        if (replace) {
          window.location.replace(to);
        } else {
          window.location.assign(to);
        }
        return;
      }

      pending = { path, replace };
      console.info(LOG, replace ? 'routerReplace queue' : 'routerPush queue', path);
      if (scheduled) {
        return;
      }
      scheduled = true;
      // setTimeout (not queueMicrotask): RR6 wires history listeners in useEffect;
      // microtasks still run "during first render" and get ignored + warned.
      window.setTimeout(() => {
        scheduled = false;
        const next = pending;
        pending = null;
        if (!next) {
          return;
        }
        console.info(LOG, next.replace ? 'routerReplace apply' : 'routerPush apply', next.path);
        navigateRef.current(next.path, next.replace ? { replace: true } : undefined);
      }, 0);
    };

    return {
      routerPush: (to: string) => enqueue(to, false),
      routerReplace: (to: string) => enqueue(to, true),
    };
  }, []);

  // ClerkProviderProps Omit can erase navigation keys in our TS resolve; runtime accepts them.
  const providerProps = {
    publishableKey,
    routerPush,
    routerReplace,
    taskUrls: {
      'choose-organization': CHOOSE_ORG_SIGN_UP,
    },
    signUpFallbackRedirectUrl: CHOOSE_ORG_SIGN_UP,
    signInFallbackRedirectUrl: CHOOSE_ORG_SIGN_IN,
    children,
  } as ComponentProps<typeof ClerkProvider>;

  return <ClerkProvider {...providerProps} />;
}
